import axios from "../../axiosClient";
import type { ZonePayload } from "src/modules/asset/components/asset-detail/asset-configuration/components/types";

export const getZonesByGateway = async (
  gatewayCode: string,
  params?: { pageNumber?: number; pageSize?: number; searchText?: string },
  signal?: any,
) => {
  const res = await axios.get(`/slpapp/GatewayZone`, {
    params: {
      gatewayCode,
      pageNumber: params?.pageNumber ?? 1,
      pageSize: params?.pageSize ?? 100,
      searchText: params?.searchText?.trim() || null,
    },
    signal,
  });
  return res?.data;
};

export const getTriggeringZones = async (assetCode: string, signal?: any) => {
  const res = await axios.get(`/slpapp/TriggeringZone/GetTriggeringZone`, {
    params: { assetCode },
    signal,
  });
  return res?.data;
};

export const setTriggeringZone = async (payload: ZonePayload, signal?: any) => {
  const res = await axios.post(`/slpapp/TriggeringZone/SetTriggeringZone`, payload, {
    signal,
  });
  return res?.data;
};

export const deleteGatewayZone = async (zoneId: number, signal?: any) => {
  const res = await axios.delete(`/slpapp/GatewayZone`, {
    params: { zoneId },
    signal,
  });
  return res?.data;
};

export const deleteTriggeringZone = async (zoneId: number, signal?: any) => {
  const res = await axios.delete(`/slpapp/TriggeringZone`, {
    params: { zoneId },
    signal,
  });
  return res?.data;
};

export const getLocationBlocks = async (signal?: any) => {
  const res = await axios.get(`/slpapp/Block/getall`, {
    params: { PageNumber: 1, pageSize: 100, SortBy: "blockName", SortDirection: "ASC" },
    signal,
  });
  return res?.data;
};

export const getUniqueFloorsWithSimilarPlan = async (blockCode: string, signal?: any) => {
  const res = await axios.get(`/slpapp/BlockToFloor/getUniqueFloorsWithSimilarPlan`, {
    params: { blockCode },
    signal,
  });
  return res?.data;
};

export const saveGatewayZone = async (payload: any, signal?: any) => {
  const res = await axios.post(`/slpapp/GatewayZone`, payload, { signal });
  return res?.data;
};

export const updateGatewayZone = async (payload: any, signal?: any) => {
  const res = await axios.put(`/slpapp/GatewayZone`, payload, { signal });
  return res?.data;
};

export type ZoneGeneralConfigurationPayload = {
  gatewayZoneId: number;
  operationModeId: number | null;
  failsafeDelay: number | null;
  rampUpTime: number | null;
  rampDownTime: number | null;
  dimLevel: number | null;
  brightLevel: number | null;
  timeDelay: number | null;
  detectionRangeId: number | null;
  photoLowerThreshold: number | null;
  photoUpperThreshold: number | null;
  photoSensing: boolean | null;
};

export type ZoneGeneralConfigurationValues = {
  operationModeId: number | null;
  failsafeDelay: number | null;
  rampUpTime: number | null;
  rampDownTime: number | null;
  dimLevel: number | null;
  brightLevel: number | null;
  timeDelay: number | null;
  detectionRangeId: number | null;
  photoLowerThreshold: number | null;
  photoUpperThreshold: number | null;
  photoSensing: boolean | null;
};

export const getZoneGeneralConfigurationParameters = async (
  gatewayZoneId: number,
  signal?: AbortSignal,
) => {
  const res = await axios.get(
    `/slpapp/GatewayZone/GetZoneGeneralConfigurationParameters`,
    { params: { gatewayZoneId }, signal },
  );
  return res?.data;
};

export type ZoneGeneralConfigurationHistoryQuery = {
  gatewayZoneId: number;
  ackstatus: string;
  pageNumber: number;
  searchText: string;
  sortBy: string;
  sortDirection: string;
  pageSize: number;
};

export const getZoneGeneralConfigurationHistory = async ({
  gatewayZoneId,
  ackstatus,
  pageNumber,
  searchText,
  sortBy,
  sortDirection,
  pageSize,
}: ZoneGeneralConfigurationHistoryQuery) => {
  const res = await axios.post(
    `/slpapp/GatewayZone/GetZoneGeneralConfigurationHistory`,
    { pageNumber, searchText : searchText || "", sortBy, sortDirection, pageSize },
    { params: { gatewayZoneId, ackstatus } },
  );
  return res?.data;
};

export const getZoneGeneralConfigurationHistoryDetail = async (
  historyId: number,
  signal?: AbortSignal,
) => {
  const res = await axios.post(
    `/slpapp/GatewayZone/GetZoneGeneralConfigurationHistoryDetail`,
    undefined,
    { params: { historyId }, signal },
  );
  return res?.data;
};

export const setZoneGeneralConfiguration = async (
  payload: ZoneGeneralConfigurationPayload,
  signal?: AbortSignal,
) => {
  const res = await axios.post(
    `/slpapp/GatewayZone/SetZoneGeneralConfiguration`,
    payload,
    { signal },
  );
  return res?.data;
};

export const saveCommandToGatewayByAssetCode = async (assetCodes: string[], signal?: any) => {
  const res = await axios.post(
    `/slpapp/GatewayZone/SaveCommandToGatewayByAssetCode`,
    { strs: assetCodes },
    { signal },
  );
  return res?.data;
};

export const getAssetsByZone = async (zoneId: number, signal?: any) => {
  const res = await axios.get(`/slpapp/GatewayZone/AssetByZone`, {
    params: { zoneId, pageNumber: 1, pageSize: 100, searchText: null },
    signal,
  });
  return res?.data;
};

export const getGatewayZoneAssets = async (
  gatewayZoneId: number,
  params: {
    pageNumber: number;
    pageSize: number;
    sortBy: string;
    sortDirection: string;
  },
  signal?: AbortSignal,
) => {
  const res = await axios.get(`/slpapp/GatewayZone/AssetByZone`, {
    params: {
      zoneId:gatewayZoneId,
      PageNumber: params.pageNumber,
      SortBy: params.sortBy,
      SortDirection: params.sortDirection,
      PageSize: params.pageSize,
    },
    signal,
  });
  return res?.data;
};

export const getAssetsByGateway = async (gatewayCode: string, searchText?: string, signal?: any) => {
  const res = await axios.get(`/slpapp/Asset/AssetsByGateway`, {
    params: {
      gatewayCode,
      searchText: searchText || null,
      PageNumber: 1,
      PageSize: 100,
    },
    signal,
  });
  return res?.data;
};

export const getChildBlocksByGateway = async (gatewayCode: string, signal?: any) => {
  const res = await axios.get(`/slpapp/GatewayZone/ChildBlockByGateway`, {
    params: { gatewayCode, PageNumber: 1, PageSize: 10 },
    signal,
  });
  return res?.data;
};

export const getDisplayLevelsByBlock = async (gatewayCode: string, blockCode: string, signal?: any) => {
  const res = await axios.get(`/slpapp/BlockToFloor/GetAllFloorPlanLevelByBlock`, {
    params: { gatewayCode, blockCode },
    signal,
  });
  return res?.data;
};

export const getGatewayGroup = async (assetCode: string, signal?: any) => {
  const res = await axios.get(`/slpapp/Asset/byParentAssetCode`, {
    params: { assetCode },
    signal,
  });
  return res?.data;
};

export type ZoneAckInfo = {
  zoneId: number;
  isAcknowledged: boolean;
  isCommandSent: boolean;
  color: string;
  statusMessage: string;
};

export const getZoneAckInfoByGateway = async (gatewayCode: string, signal?: any) => {
  const res = await axios.post(`/slpapp/GatewayZone/GetZoneAckInfoByGateway?gatewayCode=${gatewayCode}`, undefined, { signal });
  return res?.data;
};

export const getZoneUnAckInfoByGateway = async ({
  gatewayCode,
  pageNumber = 1,
  pageSize = 10,
  searchText = null,
  sortBy = "CommandSentOn",
  sortDirection = "DESC",
  zoneId,
}: {
  gatewayCode: string;
  pageNumber?: number;
  pageSize?: number;
  searchText?: string | null;
  sortBy?: string;
  sortDirection?: string;
  zoneId?: number;
}) => {
  const params: any = {
    gatewayCode,
    pageNumber,
    pageSize,
    searchText: searchText || null,
    sortBy,
    sortDirection,
  };
  if (zoneId != null) {
    params.zoneId = zoneId;
  }
  const res = await axios.post(`/slpapp/GatewayZone/GetZoneUnAckInfoByGateway`, null, { params });
  return res?.data;
};

export const saveToGatewayZone = async (gatewayCode: string, nums: number[]) => {
  const res = await axios.post(`/slpapp/GatewayZone/SaveToGatewayZone`, { nums }, { params: { gatewayCode } });
  return res?.data;
};
